import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../components/Sidebar';
import { api, type Plan } from '../api/client';
import { T } from '../styles/tokens';

type Proto = 'wireguard' | 'vless';

export default function Purchase() {
  const nav = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [proto, setProto] = useState<Proto | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.plans.list().then(setPlans).catch((e) => console.error(e));
  }, []);

  const handlePay = async () => {
    if (!proto || !plan) return;
    setError(null);
    setPaying(true);
    try {
      const order = await api.payments.create({
        plan_code: plan.code,
        protocol: proto,
      });
      if (order.auto_confirmed || order.status === 'paid') {
        setDone(true);
        return;
      }
      if (order.confirmation_url) {
        window.location.href = order.confirmation_url;
        return;
      }
      setError('Платёжная система не вернула ссылку на оплату. Попробуйте ещё раз.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Неизвестная ошибка');
    } finally {
      setPaying(false);
    }
  };

  if (done) {
    return (
      <div style={p.root}>
        <Sidebar active="purchase" />
        <main style={p.main}>
          <div style={p.successWrap}>
            <div style={p.successTag}>// готово</div>
            <div style={p.successTitle}>Подписка активирована</div>
            <div style={p.successSub}>Конфигурация отправлена в Telegram и доступна в личном кабинете.</div>
            <div style={p.successActions}>
              <button style={p.ctaBtn} onClick={() => nav('/dashboard')}>
                Мои подписки
              </button>
              <button style={p.outlineBtn} onClick={() => nav('/instructions')}>
                Инструкция по подключению
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={p.root}>
      <Sidebar active="purchase" />
      <main style={p.main}>
        <div style={p.topBar}>
          <div>
            <div style={p.pageTag}>// новая подписка</div>
            <div style={p.pageTitle}>Купить подписку</div>
          </div>
        </div>

        <div style={p.stepper}>
          {['Протокол', 'Тариф', 'Оплата'].map((label, i) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={p.stepItem}>
                <div
                  style={{
                    ...p.stepCircle,
                    ...(step > i + 1 ? p.stepDone : step === i + 1 ? p.stepActive : {}),
                  }}
                >
                  {step > i + 1 ? '✓' : `0${i + 1}`}
                </div>
                <span style={{ ...p.stepLabel, color: step >= i + 1 ? T.text : T.textMuted }}>{label}</span>
              </div>
              {i < 2 && <div style={{ ...p.stepLine, background: step > i + 1 ? T.accent : T.border }} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div style={p.content}>
            <div style={p.stepHead}>Выберите протокол</div>
            <div style={p.protoGrid}>
              {[
                {
                  id: 'wireguard' as Proto,
                  name: 'WireGuard',
                  badge: 'Скорость',
                  color: T.accent,
                  desc: 'UDP, порт 51820. Конфиг .conf + QR. Для стриминга, игр, повседневного использования.',
                  apps: 'iOS · Android · Windows · macOS',
                },
                {
                  id: 'vless' as Proto,
                  name: 'VLESS Reality',
                  badge: 'Обход DPI',
                  color: T.success,
                  desc: 'TCP, порт 443, Reality SNI. XTLS-Vision. Маскировка под HTTPS. Сервер DE.',
                  apps: 'Streisand · v2rayNG · Hiddify',
                },
              ].map((pt) => (
                <div
                  key={pt.id}
                  style={{ ...p.protoCard, ...(proto === pt.id ? { ...p.protoCardActive, borderColor: pt.color } : {}) }}
                  onClick={() => setProto(pt.id)}
                >
                  <div style={{ ...p.protoBadge, color: pt.color, borderColor: pt.color + '66' }}>{pt.badge}</div>
                  <div style={p.protoName}>{pt.name}</div>
                  <div style={p.protoDesc}>{pt.desc}</div>
                  <div style={p.protoApps}>{pt.apps}</div>
                  {proto === pt.id && <div style={{ ...p.selectedMark, color: pt.color }}>✓ Выбрано</div>}
                </div>
              ))}
            </div>
            <button style={{ ...p.ctaBtn, opacity: proto ? 1 : 0.4 }} disabled={!proto} onClick={() => setStep(2)}>
              Продолжить →
            </button>
          </div>
        )}

        {step === 2 && (
          <div style={p.content}>
            <div style={p.stepHead}>Выберите тариф</div>
            <div style={p.plansTable}>
              <div style={p.planHead}>
                <div style={p.pth}>Период</div>
                <div style={p.pth}>Цена</div>
                <div style={p.pth}>В день</div>
                <div style={p.pth} />
              </div>
              {plans.map((pl) => (
                <div
                  key={pl.code}
                  style={{ ...p.planRow, ...(plan?.code === pl.code ? p.planRowActive : {}) }}
                  onClick={() => setPlan(pl)}
                >
                  <div style={p.ptd}>
                    <span style={p.planPer}>{pl.label}</span>
                    {pl.popular && <span style={p.hotBadge}>популярный</span>}
                  </div>
                  <div style={p.ptd}>
                    <span style={p.planPrice}>{pl.price}</span>
                    <span style={p.planCur}> ₽</span>
                  </div>
                  <div style={{ ...p.ptd, fontFamily: 'DM Mono, monospace', fontSize: 12, color: T.textMuted }}>
                    {pl.per_day} ₽
                  </div>
                  <div style={{ ...p.ptd, textAlign: 'right' }}>
                    {plan?.code === pl.code ? (
                      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.accent }}>✓ выбрано</span>
                    ) : (
                      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.textMuted }}>выбрать</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div style={p.navRow}>
              <button style={p.outlineBtn} onClick={() => setStep(1)}>
                ← Назад
              </button>
              <button style={{ ...p.ctaBtn, opacity: plan ? 1 : 0.4 }} disabled={!plan} onClick={() => setStep(3)}>
                Продолжить →
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div style={p.content}>
            <div style={p.stepHead}>Оплата</div>
            <div style={p.payLayout}>
              <div>
                <div style={p.payLabel}>// способ оплаты</div>
                <div style={{ ...p.payMethodRow, ...p.payMethodRowActive }}>
                  <div style={p.payDot}>
                    <div style={p.payDotFill} />
                  </div>
                  <div>
                    <div style={p.payName}>ЮKassa</div>
                    <div style={p.payHint}>Карта · СБП · кошелёк</div>
                  </div>
                </div>
                <div style={p.payNote}>
                  После нажатия «Оплатить» откроется защищённая страница ЮKassa. Конфиг придёт в личный кабинет автоматически после подтверждения платежа.
                </div>
                {error && <div style={p.errorBox}>⚠ {error}</div>}
              </div>
              <div style={p.orderBox}>
                <div style={p.orderLabel}>// итого</div>
                <div style={p.orderRow}>
                  <span>Протокол</span>
                  <span>{proto === 'wireguard' ? 'WireGuard' : 'VLESS Reality'}</span>
                </div>
                <div style={p.orderRow}>
                  <span>Тариф</span>
                  <span>{plan?.label}</span>
                </div>
                <div style={p.orderRow}>
                  <span>Период</span>
                  <span>{plan?.days} дней</span>
                </div>
                <div style={p.orderDivider} />
                <div style={p.orderTotal}>
                  <span>К оплате</span>
                  <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, color: T.accent, fontSize: 24, letterSpacing: '-0.03em' }}>
                    {plan?.price} ₽
                  </span>
                </div>
                <button
                  style={{ ...p.ctaBtn, width: '100%', justifyContent: 'center', opacity: paying ? 0.65 : 1 }}
                  disabled={paying}
                  onClick={handlePay}
                >
                  {paying ? 'Обработка...' : `Оплатить ${plan?.price} ₽`}
                </button>
                <div style={p.orderNote}>Конфиг выдаётся автоматически после подтверждения платежа</div>
              </div>
            </div>
            <button style={p.outlineBtn} onClick={() => setStep(2)}>
              ← Назад
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

const p: Record<string, CSSProperties> = {
  root: { display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: 'DM Sans, sans-serif', color: T.text },
  main: { flex: 1, marginLeft: 220, padding: '36px 44px', boxSizing: 'border-box', maxWidth: 'calc(100vw - 220px)' },
  topBar: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 },
  pageTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 },
  pageTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 28, letterSpacing: '-0.03em', color: T.text },
  stepper: { display: 'flex', alignItems: 'center', gap: 0, marginBottom: 40, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, padding: '16px 0' },
  stepItem: { display: 'flex', alignItems: 'center', gap: 10 },
  stepCircle: { width: 26, height: 26, background: T.surfaceUp, border: `1px solid ${T.border}`, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.04em', flexShrink: 0 },
  stepActive: { background: T.accentSoft, borderColor: T.accent, color: T.accent },
  stepDone: { background: 'oklch(0.72 0.18 145 / 0.12)', borderColor: 'oklch(0.72 0.18 145)', color: 'oklch(0.72 0.18 145)' },
  stepLabel: { fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, marginRight: 16 },
  stepLine: { height: 1, flex: '1 1 40px', maxWidth: 60, marginRight: 16 },
  content: { maxWidth: 780 },
  stepHead: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 19, letterSpacing: '-0.02em', marginBottom: 20, color: T.text },
  navRow: { display: 'flex', gap: 12, marginTop: 24 },
  ctaBtn: { background: T.accent, border: 'none', borderRadius: 4, padding: '12px 26px', color: '#000', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', display: 'inline-flex', alignItems: 'center', gap: 6 },
  outlineBtn: { background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 4, padding: '11px 22px', color: T.text, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  protoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 },
  protoCard: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '24px', cursor: 'pointer', transition: 'border-color 0.15s' },
  protoCardActive: { background: 'oklch(0.72 0.22 30 / 0.05)' },
  protoBadge: { fontFamily: 'DM Mono, monospace', fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', border: '1px solid', padding: '2px 7px', borderRadius: 2, display: 'inline-block', marginBottom: 12 },
  protoName: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20, letterSpacing: '-0.02em', marginBottom: 10, color: T.text },
  protoDesc: { fontSize: 13, color: T.textSec, lineHeight: 1.6, marginBottom: 10 },
  protoApps: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.04em' },
  selectedMark: { fontFamily: 'DM Mono, monospace', fontSize: 10, marginTop: 14, letterSpacing: '0.04em' },
  plansTable: { marginBottom: 24, border: `1px solid ${T.border}`, borderRadius: 4, overflow: 'hidden' },
  planHead: { display: 'grid', gridTemplateColumns: '1fr 120px 90px 80px', background: T.surfaceUp, borderBottom: `1px solid ${T.border}`, padding: '0 20px' },
  pth: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '10px 0' },
  planRow: { display: 'grid', gridTemplateColumns: '1fr 120px 90px 80px', alignItems: 'center', borderBottom: `1px solid ${T.border}`, padding: '18px 20px', cursor: 'pointer', transition: 'background 0.1s' },
  planRowActive: { background: T.accentSoft },
  ptd: { fontSize: 14, color: T.textSec },
  planPer: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, color: T.text, letterSpacing: '-0.02em' },
  hotBadge: { marginLeft: 10, fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.accent, background: T.accentSoft, border: `1px solid ${T.accentMid}`, padding: '2px 7px', borderRadius: 2, textTransform: 'uppercase', letterSpacing: '0.08em', verticalAlign: 'middle' },
  planPrice: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20, color: T.text, letterSpacing: '-0.03em' },
  planCur: { fontSize: 13, color: T.textSec },
  payLayout: { display: 'grid', gridTemplateColumns: '1fr 300px', gap: 28, marginBottom: 24 },
  payLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 },
  payMethodRow: { display: 'flex', alignItems: 'center', gap: 12, border: `1px solid ${T.border}`, borderRadius: 4, padding: '14px 16px', cursor: 'pointer', marginBottom: 8, transition: 'border-color 0.15s' },
  payMethodRowActive: { borderColor: T.accent, background: T.accentSoft },
  payDot: { width: 16, height: 16, borderRadius: '50%', border: `2px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  payDotFill: { width: 7, height: 7, borderRadius: '50%', background: T.accent },
  payName: { fontSize: 14, fontWeight: 600, marginBottom: 2, color: T.text },
  payHint: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted },
  payNote: { marginTop: 14, fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, lineHeight: 1.7, letterSpacing: '0.03em' },
  errorBox: { marginTop: 14, padding: '10px 14px', border: '1px solid oklch(0.58 0.19 27 / 0.5)', background: 'oklch(0.58 0.19 27 / 0.08)', color: 'oklch(0.72 0.19 27)', fontFamily: 'DM Mono, monospace', fontSize: 11, borderRadius: 4 },
  orderBox: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '22px' },
  orderLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 },
  orderRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.textSec, marginBottom: 8 },
  orderDivider: { height: 1, background: T.border, margin: '14px 0' },
  orderTotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 },
  orderNote: { marginTop: 12, fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, textAlign: 'center', lineHeight: 1.6, letterSpacing: '0.04em' },
  successWrap: { maxWidth: 440, margin: '80px auto 0', textAlign: 'center' },
  successTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.success, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 },
  successTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, letterSpacing: '-0.03em', marginBottom: 12, color: T.text },
  successSub: { fontSize: 15, color: T.textSec, lineHeight: 1.7, marginBottom: 32 },
  successActions: { display: 'flex', flexDirection: 'column', gap: 10 },
};
